import { useState, useEffect, useCallback, useMemo, type ChangeEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  useTheme,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Security,
  Clear,
  Refresh,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { usePermissions } from '../contexts/PermissionContext';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { useListSearchParams } from '../hooks/useListSearchParams';
import LinkPagination from '../components/LinkPagination';
import LinkTableCell from '../components/LinkTableCell';
import FilteredListLayout from '../components/FilteredListLayout';
import ListActionButton from '../components/ListActionButton';
import type { PermissionGroup } from '../types';
import usePageTitle from '../hooks/usePageTitle';

export default function GroupsPage() {
  const { t, i18n } = useTranslation(['pages', 'common', 'permissions']);
  usePageTitle(t('pages:groups.title'));
  const columnTitle = (label?: string) => (label ?? '').toLocaleUpperCase(i18n.language);
  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalGroups, setTotalGroups] = useState(0);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const flatButtonSx = {
    textTransform: 'none',
    boxShadow: 'none',
  };
  const filterInputSx = {
    borderRadius: '10px',
    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    },
    '& .MuiSelect-select': {
      padding: '11px 12px',
    },
    '& .MuiInputBase-input': {
      padding: '11px 12px',
    },
  };

  const {
    filters,
    page,
    rowsPerPage,
    ordering,
    setParams,
    setPage,
    setRowsPerPage,
    getPageLink,
  } = useListSearchParams({
    filters: ['scope', 'search'],
    defaultPage: 1,
    defaultPageSize: 10,
    defaultOrdering: '-created_at',
  });

  const scopeFilterValue = filters.scope ?? '';
  const searchFilterValue = filters.search ?? '';
  const orderingParam = ordering || '-created_at';
  
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('groups_create');
  const canUpdate = hasPermission('groups_update');
  const canDelete = hasPermission('groups_delete');

  // Confirmation dialog hook
  const { confirm, DialogComponent: ConfirmDialogComponent } = useConfirmDialog();

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        page: page + 1,
        page_size: rowsPerPage,
        ordering: orderingParam,
      };

      if (scopeFilterValue) {
        params.scope = scopeFilterValue;
      }

      if (searchFilterValue) {
        params.search = searchFilterValue;
      }

      const response = await api.get('/api/permission-groups/', { params });
      const data = response.data.results ?? response.data;
      setGroups(Array.isArray(data) ? data : []);
      setTotalGroups(response.data.count ?? (Array.isArray(data) ? data.length : 0));
    } catch (err) {
      setError(t('pages:groups.errors.loading'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [orderingParam, page, rowsPerPage, scopeFilterValue, searchFilterValue, t]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const scopeOptions = useMemo(
    () => [
      { value: 'system', label: t('pages:groups.scopeSystem') },
      { value: 'tenant', label: t('pages:groups.scopeTenant') },
    ],
    [t],
  );

  const headerActions = (
    <>
      <ListActionButton
        variant="outlined"
        startIcon={<Refresh />}
        onClick={fetchGroups}
      >
        {t('common:actions.refresh')}
      </ListActionButton>
      {canCreate && (
        <ListActionButton
          variant="contained"
          startIcon={<Add />}
          component={RouterLink}
          to="/groups/new"
        >
          {t('pages:groups.new')}
        </ListActionButton>
      )}
    </>
  );

  const handleDelete = async (id: string, isSystem: boolean) => {
    if (isSystem) {
      setError('Cannot delete system groups');
      return;
    }

    const confirmed = await confirm({
      title: t('pages:groups.deleteGroup.title'),
      message: t('pages:groups.deleteGroup.message'),
      type: 'error',
      icon: <Delete color="error" />,
      confirmIcon: <Delete />,
      confirmText: t('pages:groups.deleteGroup.confirm'),
    });
    if (!confirmed) return;

    try {
      await api.delete(`/api/permission-groups/${id}/`);
      fetchGroups();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || t('pages:groups.errors.deleting'));
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  const filterControls = (
    <>
      <TextField
        size="small"
        label={t('common:actions.search')}
        placeholder={t('pages:groups.filters.searchPlaceholder')}
        value={searchFilterValue}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const value = event.target.value;
          setParams({ search: value || undefined }, { resetPage: true });
        }}
        sx={{ ...filterInputSx, minWidth: 220, flex: '1 1 220px' }}
      />
      <FormControl size="small" sx={{ minWidth: 200, ...filterInputSx }}>
        <InputLabel id="groups-scope-filter-label">
          {t('pages:groups.filters.scopeLabel')}
        </InputLabel>
        <Select
          labelId="groups-scope-filter-label"
          value={scopeFilterValue}
          label={t('pages:groups.filters.scopeLabel')}
          onChange={(event: SelectChangeEvent<string>) => {
            const value = event.target.value;
            setParams({ scope: value || undefined }, { resetPage: true });
          }}
        >
          <MenuItem value="">
            <em>{t('common:common.all')}</em>
          </MenuItem>
          {scopeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Clear />}
        disabled={!searchFilterValue && !scopeFilterValue}
        onClick={() => {
          if (!searchFilterValue && !scopeFilterValue) return;
          setParams({ search: null, scope: null }, { resetPage: true });
        }}
        sx={{
          ...flatButtonSx,
          color: theme.palette.primary.main,
          borderColor: alpha(theme.palette.primary.main, 0.6),
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        {t('common:actions.clear')}
      </Button>
    </>
  );

  const table = (
    <Table>
      <TableHead>
        <TableRow
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            '& th': {
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              fontSize: '0.75rem',
            },
          }}
        >
          <TableCell>{columnTitle(t('pages:groups.table.name'))}</TableCell>
          <TableCell>{columnTitle(t('pages:groups.table.scope'))}</TableCell>
          <TableCell>{columnTitle(t('pages:groups.table.permissions'))}</TableCell>
          <TableCell>{columnTitle(t('pages:groups.table.type'))}</TableCell>
          <TableCell align="right">{columnTitle(t('pages:groups.table.actions'))}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {groups.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} align="center">
              <Box py={4}>
                <Security sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">{t('pages:groups.noGroupsConfigured')}</Typography>
              </Box>
            </TableCell>
          </TableRow>
        ) : (
          groups.map((group) => (
            <TableRow
              key={group.id}
              hover
              sx={{
                transition: 'background 0.2s',
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <LinkTableCell to={`/groups/${group.id}`}>
                <Box>
                  <Typography fontWeight={600}>{group.name}</Typography>
                  {group.description && (
                    <Typography variant="caption" color="text.secondary">
                      {group.description}
                    </Typography>
                  )}
                </Box>
              </LinkTableCell>
              <TableCell>
                <Chip
                  label={group.scope === 'system' ? t('pages:groups.table.scopeSystem') : t('pages:groups.table.scopeTenant')}
                  color={group.scope === 'system' ? 'primary' : 'default'}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {group.permissions.length} {t('pages:groups.table.permissionsCount')}
                </Typography>
              </TableCell>
              <TableCell>
                {group.isSystem ? (
                  <Chip label={t('pages:groups.table.system')} color="warning" size="small" />
                ) : (
                  <Chip label={t('pages:groups.table.custom')} size="small" />
                )}
              </TableCell>
              <TableCell align="right">
                {canUpdate && (
                  <IconButton size="small" component={RouterLink} to={`/groups/${group.id}`}>
                    <Edit />
                  </IconButton>
                )}
                {canDelete && !group.isSystem && (
                  <IconButton size="small" color="error" onClick={() => handleDelete(group.id, group.isSystem)}>
                    <Delete />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  const tableFooter =
    totalGroups > 0 ? (
      <LinkPagination
        component="div"
        count={totalGroups}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
        }}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage={t('common:pagination.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) =>
          count !== -1
            ? `${from}-${to} ${t('common:common.of')} ${count}`
            : `${from}-${to} ${t('common:common.of')} ${t('common:common.total')} >${to}`
        }
        getItemAriaLabel={(type) => {
          if (type === 'first') return t('common:pagination.firstPage');
          if (type === 'last') return t('common:pagination.lastPage');
          if (type === 'next') return t('common:pagination.nextPage');
          return t('common:pagination.previousPage');
        }}
        getPageLink={getPageLink}
      />
    ) : undefined;

  return (
    <Box>
      <FilteredListLayout
        headerProps={{
          icon: <Security sx={{ fontSize: 32, color: 'primary.main' }} />,
          title: t('pages:groups.title'),
          subtitle: t('pages:groups.subtitle'),
          actions: headerActions,
        }}
        filterControls={filterControls}
        table={table}
        tableFooter={tableFooter}
        errorContent={
          error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
              {error}
            </Alert>
          )
        }
      />

      {/* Confirmation Dialog */}
      {ConfirmDialogComponent}
    </Box>
  );
}
